import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listBuyerOpenCarts, listCartLines } from '@pc/db';
import { useAuth } from '../auth/AuthProvider';

/**
 * Header cart icon. Renders a count badge that is the SUM of qty across
 * every open cart (one per vendor). Counts come from a 60s polling
 * query so a cart edit on another tab eventually reflects without a
 * page reload. We deliberately keep the query cheap (RLS already scopes
 * the open-carts list, and lines are <=200 per cart per the hard cap).
 *
 * The badge hides when count = 0 so the icon doesn't feel cluttered.
 */
export function CartIconBadge() {
  const { session } = useAuth();
  const userId = session?.user.id;

  const cartsQ = useQuery({
    queryKey: ['shop-buyer-open-carts', userId],
    queryFn: () => listBuyerOpenCarts(),
    enabled: Boolean(userId),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const carts = cartsQ.data ?? [];
  const lineQueries = useQuery({
    queryKey: ['shop-buyer-cart-lines-all', userId, carts.map((c) => c.id).join(',')],
    enabled: Boolean(userId) && carts.length > 0,
    queryFn: async () => {
      const all = await Promise.all(carts.map((c) => listCartLines(c.id)));
      return all.flat();
    },
    staleTime: 30_000,
  });
  const totalQty = (lineQueries.data ?? []).reduce((sum, l) => sum + l.qty, 0);

  if (!userId) return null;

  return (
    <Link
      to="/cart"
      title="Cart"
      className="relative inline-flex items-center justify-center w-9 h-9 border border-rule-strong hover:border-ink text-ink"
    >
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 4h2l2.2 11.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.5L21 7H6" />
        <circle cx="9" cy="20" r="1.4" />
        <circle cx="18" cy="20" r="1.4" />
      </svg>
      {totalQty > 0 ? (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-clay text-paper text-[10px] font-medium leading-[18px] text-center tabular-nums"
          aria-label={`${totalQty} item(s) in cart`}
        >
          {totalQty > 99 ? '99+' : totalQty}
        </span>
      ) : null}
    </Link>
  );
}
